/* OrbitFlow navigation model — sidebar groups.
   Matches the design: unlabelled primary group at top, then labelled
   groups (Work, Tracking, Collab, Game) each with their accent dot. */

import {
  Home, User, CheckSquare, Layers, List,
  Clock, Calendar, StickyNote, Trophy, Settings, Users, Rocket,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface NavPage {
  name: string
  href: string
  icon: LucideIcon
  /** match pathname exactly (used for the "/" root) */
  exact?: boolean
  /** only visible to users with the admin role */
  adminOnly?: boolean
  /** optional pill badge text (e.g. "7") */
  badge?: string
}

export interface NavSection {
  id: string
  /** label shown above the items; omit for the primary unlabelled group */
  name?: string
  /** accent colour for this section (used by group dot + group context) */
  accent: string
  pages: NavPage[]
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'primary',
    accent: 'var(--of-violet)',
    pages: [
      { name: 'Home',    href: '/',     icon: Home,        exact: true },
      { name: 'My page', href: '/my',   icon: User },
      { name: 'Todo',    href: '/todo', icon: CheckSquare },
    ],
  },
  {
    id: 'work',
    name: 'Work',
    accent: '#5E5CE6',
    pages: [
      { name: 'Projects', href: '/projects', icon: Layers },
      { name: 'Issues',   href: '/issues',   icon: List },
    ],
  },
  {
    id: 'tracking',
    name: 'Tracking',
    accent: '#34C4FF',
    pages: [
      { name: 'Time',     href: '/time',     icon: Clock },
      { name: 'Calendar', href: '/calendar', icon: Calendar },
      { name: 'Releases', href: '/releases', icon: Rocket },
    ],
  },
  {
    id: 'collab',
    name: 'Collab',
    accent: '#FF3B8A',
    pages: [
      { name: 'Notes', href: '/notes', icon: StickyNote },
    ],
  },
  {
    id: 'game',
    name: 'Game',
    accent: '#FF7A1A',
    pages: [
      { name: 'Trophy room', href: '/gamification', icon: Trophy },
    ],
  },
  {
    id: 'settings',
    name: 'Settings',
    accent: '#A4ADBE',
    pages: [
      // exact:true on Settings so /settings/users doesn't also light up the
      // Settings row (Users has its own entry).
      { name: 'Settings', href: '/settings',       icon: Settings, exact: true },
      { name: 'Users',    href: '/settings/users', icon: Users, adminOnly: true },
    ],
  },
]

/** Flat list of every page across all sections (for the command palette). */
export const ALL_NAV_PAGES: (NavPage & { section: string })[] =
  NAV_SECTIONS.flatMap((s) => s.pages.map((p) => ({ ...p, section: s.name ?? 'Home' })))

/** Does `pathname` belong to this page? */
export function isPageActive(page: NavPage, pathname: string): boolean {
  if (page.exact) return pathname === page.href
  return pathname === page.href || pathname.startsWith(page.href + '/')
}

/** The section the current pathname belongs to (most specific match wins). */
export function activeSection(pathname: string): NavSection | undefined {
  let best: { section: NavSection; len: number } | undefined
  for (const section of NAV_SECTIONS) {
    for (const page of section.pages) {
      if (isPageActive(page, pathname)) {
        const len = page.href.length
        if (!best || len > best.len) best = { section, len }
      }
    }
  }
  return best?.section
}

/** The specific page within a section that matches the pathname. */
export function activePage(section: NavSection, pathname: string): NavPage | undefined {
  let best: { page: NavPage; len: number } | undefined
  for (const page of section.pages) {
    if (isPageActive(page, pathname)) {
      const len = page.href.length
      if (!best || len > best.len) best = { page, len }
    }
  }
  return best?.page
}
